import type {
    ServerCmsTypesFilterFacet as FilterFacet,
    ServerCmsTypesProductSummary as ProductSummary,
} from "@reactivated";

/**
 * Selected filter state: facet slug → array of selected value slugs.
 * Empty array (or missing key) = no filter applied for that facet.
 */
export type SelectedFilters = Record<string, string[]>;

/**
 * Client-side product filtering.
 * - AND across facets: product must match every facet that has selections
 * - OR within a facet: product must match at least one selected value
 */
export const filterProducts = (
    products: ProductSummary[],
    selectedFilters: SelectedFilters,
): ProductSummary[] => {
    const activeFilters = Object.entries(selectedFilters).filter(
        ([, values]) => values.length > 0,
    );
    if (activeFilters.length === 0) return products;

    return products.filter((product) =>
        activeFilters.every(([facetSlug, selectedValues]) =>
            product.facet_values.some(
                (facetValue) =>
                    facetValue.facet_slug === facetSlug &&
                    selectedValues.includes(facetValue.value_slug),
            ),
        ),
    );
};

/**
 * Recalculate facet value counts based on the current filter state.
 *
 * For each facet, counts are computed against products filtered by all OTHER
 * active facets (excluding the current one). This prevents a facet's own
 * selections from zeroing out its sibling values.
 */
export const recalculateFacetCounts = (
    facets: FilterFacet[],
    products: ProductSummary[],
    selectedFilters: SelectedFilters,
): FilterFacet[] => {
    const activeEntries = Object.entries(selectedFilters).filter(
        ([, values]) => values.length > 0,
    );
    if (activeEntries.length === 0) return facets;

    return facets.map((facet) => {
        // Filter by everything EXCEPT this facet
        const otherFilters = activeEntries.filter(
            ([slug]) => slug !== facet.slug,
        );

        const baseProducts =
            otherFilters.length === 0
                ? products
                : products.filter((product) =>
                      otherFilters.every(([facetSlug, selectedValues]) =>
                          product.facet_values.some(
                              (facetValue) =>
                                  facetValue.facet_slug === facetSlug &&
                                  selectedValues.includes(
                                      facetValue.value_slug,
                                  ),
                          ),
                      ),
                  );

        return {
            ...facet,
            values: facet.values.map((value) => ({
                ...value,
                product_count: baseProducts.filter((product) =>
                    product.facet_values.some(
                        (facetValue) =>
                            facetValue.facet_slug === facet.slug &&
                            facetValue.value_slug === value.slug,
                    ),
                ).length,
            })),
        };
    });
};
